import clsx from 'clsx';
import { TrendingDown, TrendingUp } from 'lucide-react';
import InfoTip from './InfoTip';

/**
 * Stat tile used across the dashboard. Renders an icon chip on the right,
 * an optional trend pill, an optional hint line, and an optional info
 * tooltip next to the label (for jargon explanations).
 */
export default function StatCard({
  label,
  value,
  hint,
  trend, // optional: { value: '+12%', positive: true }
  icon: Icon,
  variant = 'brand', // 'brand' | 'cyan' | 'green' | 'amber' | 'rose' | 'muted'
  info, // optional: { title, body } — shows a hover-able ⓘ next to the label
  className = '',
}) {
  const variants = {
    brand: 'from-brand-50 to-brand-100/40 text-brand-700',
    cyan: 'from-cyan-500/10 to-cyan-500/5 text-cyan-600',
    green: 'from-emerald-50 to-emerald-100/40 text-emerald-700',
    amber: 'from-amber-50 to-amber-100/40 text-amber-700',
    rose: 'from-rose-50 to-rose-100/40 text-rose-600',
    muted: 'from-gray-100 to-gray-50 text-ink-700',
  };
  return (
    <div className={clsx('card-hover relative', className)}>
      {/* The decorative gradient blur lives inside its own clipping wrapper
          so the outer card can stay overflow-visible — otherwise InfoTip
          tooltips positioned above the card edge get clipped invisible. */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
        <div
          className={clsx(
            'absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br opacity-60 blur-2xl',
            variants[variant],
          )}
        />
      </div>
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-400 flex items-center gap-1">
            <span className="truncate">{label}</span>
            {info && <InfoTip title={info.title} body={info.body} />}
          </div>
          <div className="mt-1.5 text-3xl font-bold tracking-tight text-ink-900">
            {value}
          </div>
        </div>
        {Icon && (
          <div
            className={clsx(
              'flex-none h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br',
              variants[variant],
            )}
          >
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      <div className="relative mt-3 flex items-center gap-2">
        {trend && (
          <span
            className={clsx(
              'pill',
              trend.positive
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-rose-50 text-rose-600',
            )}
          >
            {trend.positive ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {trend.value}
          </span>
        )}
        {hint && <span className="text-xs text-ink-400 truncate">{hint}</span>}
      </div>
    </div>
  );
}
